import React, { useState, useEffect, useCallback } from "react";
import { Layout } from "../components/Layout";
import { useDiscoverImages } from "../hooks/useDiscoverImages";
import BentoImageGrid from "../components/BentoImageGrid";
import { supabase } from "../lib/supabase";
import { useAuthContext } from "../contexts/auth";
import { useNavigate } from "react-router-dom";
import type { DiscoverImage } from "../hooks/useDiscoverImages";
import { PaperSection } from "../components/PaperSection";
import FeedbackWidget from "../components/FeedbackWidget";

// Simple icon component to replace Tabler IconAlertCircle
const IconAlertCircle = ({ size = 24, className = "" }) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width={size}
		height={size}
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		className={className}
	>
		<circle cx="12" cy="12" r="10"></circle>
		<line x1="12" y1="8" x2="12" y2="12"></line>
		<line x1="12" y1="16" x2="12.01" y2="16"></line>
	</svg>
);

export default function Discover() {
	const {
		images,
		loading,
		error,
		hasMore,
		loadMore,
		checkBucketAccess,
		stats,
	} = useDiscoverImages();
	// Debug state commented out
	// const [debug, setDebug] = useState<any>(null);
	const { session } = useAuthContext();
	// const [directTestResult, setDirectTestResult] = useState<any>(null);
	// const [directTestLoading, setDirectTestLoading] = useState(false);
	const [showFilenameAlert, setShowFilenameAlert] = useState(true);
	const navigate = useNavigate();

	// Handle image click
	const handleImageClick = (image: DiscoverImage) => {
		// Navigate to try-on page with the selected image
		navigate("/try-on", {
			state: {
				selectedImage: image,
				fromDiscover: true,
			},
		});
	};

	// Run direct bucket access test
	/*
	const runDirectTest = async () => {
		setDirectTestLoading(true);
		try {
			const result = await checkBucketAccess();
			setDirectTestResult(result);
		} catch (err) {
			setDirectTestResult({
				error: err instanceof Error ? err.message : "Unknown error",
			});
		} finally {
			setDirectTestLoading(false);
		}
	};
	*/

	// Check if any images have special characters in their filenames
	const hasSpecialCharacterFilenames = useCallback(() => {
		return images.some(
			(img) =>
				img.filename.includes("?") ||
				img.filename.includes("&") ||
				img.filename.includes("#") ||
				img.filename.includes("+"),
		);
	}, [images]);

	// Collect debugging information
	const collectDebugInfo = useCallback(async () => {
		try {
			// Include authentication info
			const authData = {
				authenticated: !!session,
				userId: session?.user?.id || "Not logged in",
				userEmail: session?.user?.email || "No email",
				sessionExpires: session?.expires_at
					? new Date(session.expires_at * 1000).toLocaleString()
					: "No expiry",
			};

			// Check Supabase connection
			const { data: buckets, error: bucketsError } =
				await supabase.storage.listBuckets();

			// Check for discover bucket
			const discoverBucket = buckets?.find(
				(bucket) => bucket.name === "discover",
			);

			// Collect list of files in discover bucket if it exists
			let files = null;
			let fileError = null;

			if (discoverBucket) {
				const { data: fileData, error: filesError } = await supabase.storage
					.from("discover")
					.list("");

				files = fileData;
				fileError = filesError;
			}

			// Test URL generation
			let testUrl = null;
			if (files && files.length > 0) {
				const { data: urlData } = supabase.storage
					.from("discover")
					.getPublicUrl(files[0].name);

				testUrl = urlData.publicUrl;

				// Test direct fetch of the image??
				// comment the way not the what
				try {
					const testFetch = await fetch(testUrl, { method: "HEAD" });
				} catch (fetchErr) {
					// Silently handle fetch errors
				}
			}

			// Debugging information commented out
			// setDebug({
			// 	auth: authData,
			// 	supabaseConnected: !!buckets,
			// 	buckets: buckets?.map((b) => b.name),
			// 	bucketsError: bucketsError ? bucketsError.message : null,
			// 	discoverBucketExists: !!discoverBucket,
			// 	discoverBucketPublic: discoverBucket?.public,
			// 	files: files?.length,
			// 	fileNames: files?.slice(0, 5).map((f) => f.name),
			// 	fileError: fileError ? fileError.message : null,
			// 	testUrl,
			// });
		} catch (err) {
			// Debugging information commented out
			// setDebug({
			// 	error:
			// 		err instanceof Error
			// 			? err.message
			// 			: "Unknown error collecting debug info",
			// });
		}
	}, [session]);

	useEffect(() => {
		if (images.length === 0 && !loading && !error) {
			collectDebugInfo();
		}
	}, [images, loading, error, collectDebugInfo]);

	return (
		<Layout>
			<div className="space-y-4 sm:space-y-6 px-4 sm:px-0">
				<div>
					<h1 className="text-2xl sm:text-3xl font-display page-title">
						Discover
					</h1>
					<p className="text-gray-600 mt-1 text-sm sm:text-base">
						Explore our curated collection of inspiration and try on your
						favorite looks.
					</p>
				</div>

				{/* Alert about special characters in filenames */}
				{/* {showFilenameAlert &&
					images.length > 0 &&
					hasSpecialCharacterFilenames() && (
						<PaperSection variant="super-heavy" className="border-l-4 border-yellow-400" texturedEdge={true}>
							<div className="flex items-start">
								<div className="flex-shrink-0 mt-0.5">
									<IconAlertCircle aria-hidden="true" size={18} className="text-yellow-500" />
								</div>
								<div className="ml-3">
									<h3 className="text-xs sm:text-sm font-medium text-yellow-800">
										Note about image filenames
									</h3>
									<div className="mt-1 text-xs sm:text-sm text-yellow-700">
										<p>
											Some of your images have special characters in their
											filenames (like <strong>?</strong>, <strong>&amp;</strong>
											, <strong>#</strong>). These can sometimes cause display
											issues. Consider renaming files to remove special
											characters.
										</p>
										<p className="mt-1 text-xs">
											Images with special characters are marked with a yellow
											indicator in the top-right corner.
										</p>
									</div>
									<div className="mt-2">
										<button
											onClick={() => setShowFilenameAlert(false)}
											className="text-xs text-yellow-800 hover:text-haus-light-olive font-medium focus:outline-none"
										>
											Dismiss
										</button>
									</div>
								</div>
							</div>
						</PaperSection>
					)} */}

				{error ? (
					<PaperSection variant="super-heavy" className="border-l-4 border-red-400 text-red-600">
						<p>Error: {error}</p>
						<button
							onClick={loadMore}
							className="mt-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-haus-dark-brown/10 hover:bg-haus-dark-brown/20 rounded transition-colors text-sm"
							aria-label="Try loading images again"
						>
							Try Again
						</button>
					</PaperSection>
				) : images.length === 0 && !loading ? (
					<PaperSection variant="super-heavy" className="rounded-xl sm:rounded-2xl py-6 sm:py-8 flex flex-col items-center justify-center">
						<div className="w-12 h-12 sm:w-16 sm:h-16 bg-white/70 rounded-full flex items-center justify-center mb-3 sm:mb-4">
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
								className="text-gray-400"
								aria-hidden="true"
							>
								<circle cx="11" cy="11" r="8"></circle>
								<line x1="21" y1="21" x2="16.65" y2="16.65"></line>
							</svg>
						</div>
						<h3 className="text-lg sm:text-xl font-semibold mb-2">
							No Images Found
						</h3>
						<p className="text-gray-600 text-sm sm:text-base text-center max-w-md mb-4 sm:mb-6">
							No images are available in the discover section yet.
						</p>

						{/* Debug Information Directly in Discover Page */}
						{/* Debugging information commented out
						<div className="border-t border-gray-200 mt-4 pt-4">
							<h4 className="text-sm font-medium mb-2">Debug Information</h4>

							<div className="mb-3">
								<div className="flex justify-between items-center mb-2">
									<span className="text-xs font-medium">
										Direct Bucket Access Test
									</span>
									<button
										onClick={runDirectTest}
										disabled={directTestLoading}
										className="px-2 py-1 bg-gray-100 hover:bg-gray-200 text-xs rounded"
									>
										{directTestLoading ? "Testing..." : "Run Test"}
									</button>
								</div>

								{directTestResult && (
									<div className="text-xs mt-1 p-2 bg-gray-50 rounded">
										{directTestResult.error ? (
											<span className="text-red-500">{directTestResult.error}</span>
										) : (
											<pre className="whitespace-pre-wrap">
												{JSON.stringify(directTestResult, null, 2)}
											</pre>
										)}
									</div>
								)}
							</div>

							<details className="mt-3">
								<summary className="cursor-pointer text-xs sm:text-sm text-gray-500 hover:text-gray-700">
									View Raw Debug Data
								</summary>
								<pre className="mt-2 bg-gray-100 p-2 rounded overflow-auto whitespace-pre-wrap text-xs">
									{JSON.stringify(debug, null, 2)}
								</pre>
							</details>
						</div>
						*/}
					</PaperSection>
				) : (
					// Render the bento grid with our images
					<BentoImageGrid
						images={images}
						loading={loading}
						hasMore={hasMore}
						onLoadMore={loadMore}
						stats={stats}
						onImageClick={handleImageClick}
					/>
				)}
			</div>
			<FeedbackWidget />
		</Layout>
	);
}
